// src/features/resources/api.ts
// Direct resourceApi call sites for endpoints whose generated store wrapper
// (@core/stores/resource/ResourceStore) drops its query parameters —
// SearchResources(), GetExternalResourceByURL(), ListExternalResources(),
// ListResourceTypes() and UnlinkResources() are all generated with the
// underlying request/query data silently dropped (the store method calls
// the api function with zero arguments even though the api function itself
// accepts a params/data object). Same category of codegen gap already
// documented for resource.UnlinkResources in ChatDetailPage.tsx — call the
// api layer directly here instead of through the store, and pass real
// arguments through.

import { resourceApi } from '@core/api';
import { urnToEntity } from './logic';
import type { RequestOptions } from '@core/api/client';
import type { Entity } from '@core/types/entity';
import type {
  ExternalResourceResponse,
  LinkDirection,
  ListExternalResourcesResponse,
  ListLinksResponse,
  ListResourceTypesResponse,
  SearchResourcesResponse,
} from '@core/types/resource';

export async function searchResources(query: string, limit = 20): Promise<SearchResourcesResponse> {
  return resourceApi.SearchResources({ q: query, limit });
}

export async function listLinksByDirection(
  resourceType: string,
  resourceId: string,
  direction: LinkDirection,
  includeResources = true,
): Promise<ListLinksResponse> {
  return resourceApi.ListLinks(resourceType, resourceId, {
    pagination: { cursor: '', limit: 50, direction: 'forward' },
    sort: [],
    view: 0,
    direction,
    include_resources: includeResources,
  } as any);
}

export async function getExternalResourceByURL(url: string): Promise<ExternalResourceResponse> {
  return resourceApi.GetExternalResourceByURL({ url });
}

export async function listExternalResources(search?: string, domain?: string): Promise<ListExternalResourcesResponse> {
  const params: any = {
    pagination: { cursor: '', limit: 50, direction: 'forward' },
    sort: [],
    view: 0,
  };
  if (search) params.search = search;
  if (domain) params.domain = domain;
  return resourceApi.ListExternalResources(params);
}

export async function listResourceTypes(productId?: string): Promise<ListResourceTypesResponse> {
  const params: any = {
    pagination: { cursor: '', limit: 100, direction: 'forward' },
    sort: [],
    view: 0,
  };
  if (productId) params.product_id = productId;
  return resourceApi.ListResourceTypes(params);
}

/**
 * Unlink two resources. `UnlinkResources` is generated as a bare HTTP
 * delete with no way to pass a request body — same gap as
 * attachment.DeleteAttachmentsBulk (see docs/ux/attachment-ia.md). Uses the
 * same escape hatch: RequestOptions extends RequestInit, so a JSON body
 * passed through `options.body` reaches fetch() correctly.
 */
export async function unlinkResources(source: Entity, target: Entity, relationType?: string): Promise<void> {
  // Callers historically pass `{urn}` objects; the wire Entity has no urn
  // field, so translate to parsed entity parts before sending.
  const src = (source as { urn?: string }).urn ? urnToEntity((source as { urn?: string }).urn) : source;
  const tgt = (target as { urn?: string }).urn ? urnToEntity((target as { urn?: string }).urn) : target;
  const options: RequestOptions = {
    body: JSON.stringify({ source: src, target: tgt, relation_type: relationType }),
    headers: { 'Content-Type': 'application/json' },
  };
  await resourceApi.UnlinkResources(options);
}
